/**
 * Form Validation Utilities
 * Centralized validation schemas and utilities using Yup for form validation
 */

import * as yup from 'yup';
import { VALIDATION_PATTERNS, ManualEntryForm } from '@defs/forms';
import type { BagSize, ConsumptionUnit } from '@defs/api';
import { VALIDATION_RULES } from '@constants/index';

// ============================================================================
// Custom Validation Methods
// ============================================================================

// Extend Yup with custom validation methods
declare module 'yup' {
  interface StringSchema<TType, TContext, TDefault, TFlags> {
    strongPassword(
      message?: string,
    ): StringSchema<TType, TContext, TDefault, TFlags>;
    phoneNumber(
      message?: string,
    ): StringSchema<TType, TContext, TDefault, TFlags>;
    postalCode(
      message?: string,
    ): StringSchema<TType, TContext, TDefault, TFlags>;
    website(message?: string): StringSchema<TType, TContext, TDefault, TFlags>;
  }
}

// Add custom validation methods
yup.addMethod(
  yup.string,
  'strongPassword',
  function (
    message = 'La contraseña debe tener al menos 8 caracteres, incluyendo mayúsculas, minúsculas, números y símbolos',
  ) {
    return this.matches(VALIDATION_PATTERNS.strongPassword, message);
  },
);

yup.addMethod(
  yup.string,
  'phoneNumber',
  function (message = 'Número de teléfono inválido') {
    return this.matches(VALIDATION_PATTERNS.phone, {
      message,
      excludeEmptyString: true,
    });
  },
);

yup.addMethod(
  yup.string,
  'postalCode',
  function (message = 'Código postal inválido') {
    return this.matches(VALIDATION_PATTERNS.postalCode, message);
  },
);

yup.addMethod(yup.string, 'website', function (message = 'URL inválida') {
  return this.matches(VALIDATION_PATTERNS.website, message);
});

// ============================================================================
// Common Field Schemas
// ============================================================================

export const SUPPORTED_CURRENCY_CODES: readonly string[] = [
  'ARS',
  'EUR',
  'USD',
  'MXN',
  'GBP',
  'CHF',
  'CAD',
  'AUD',
  'JPY',
];

export const commonSchemas = {
  // Basic fields
  email: yup
    .string()
    .email('Email inválido')
    .required('Email es requerido')
    .trim(),

  password: yup
    .string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .required('Contraseña es requerida'),

  strongPassword: yup
    .string()
    .strongPassword()
    .required('Contraseña es requerida'),

  confirmPassword: (passwordField = 'password') =>
    yup
      .string()
      .oneOf([yup.ref(passwordField)], 'Las contraseñas no coinciden')
      .required('Confirmar contraseña es requerido'),

  name: yup
    .string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(50, 'El nombre no puede tener más de 50 caracteres')
    .trim(),

  requiredName: yup
    .string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(50, 'El nombre no puede tener más de 50 caracteres')
    .required('Nombre es requerido')
    .trim(),

  phone: yup.string().phoneNumber().trim(),

  website: yup.string().website().trim(),
  // Restaurant specific fields
  restaurantName: yup
    .string()
    .min(2, 'El nombre del restaurante debe tener al menos 2 caracteres')
    .max(100, 'El nombre del restaurante no puede tener más de 100 caracteres')
    .required('Nombre del restaurante es requerido')
    .trim(),

  country: yup
    .string()
    .length(2, 'Código de país debe tener exactamente 2 caracteres')
    .matches(/^[A-Z]{2}$/, 'Código de país debe ser válido (ej: ES, FR, IT)')
    .required('País es requerido'),

  city: yup
    .string()
    .min(2, 'Región/Provincia/Estado debe tener al menos 2 caracteres')
    .required('Región/Provincia/Estado es requerida')
    .trim(),

  address: yup
    .string()
    .max(255, 'La dirección no puede tener más de 255 caracteres')
    .trim(),

  cuisineType: yup.string().required('Tipo de cocina es requerido'),

  dailyCovers: yup
    .number()
    .positive('El número de cubiertos debe ser positivo')
    .integer('El número de cubiertos debe ser un entero')
    .min(
      VALIDATION_RULES.NUMERIC.MIN_POSITIVE_INTEGER,
      'Debe tener al menos 1 cubierto',
    )
    .max(
      VALIDATION_RULES.NUMERIC.MAX_COVERS,
      'Número de cubiertos demasiado alto',
    )
    .required('Número de cubiertos diarios es requerido'),

  // Consumption fields
  consumption: yup
    .number()
    .min(0, 'El consumo no puede ser negativo')
    .max(
      VALIDATION_RULES.NUMERIC.MAX_CONSUMPTION,
      'El consumo parece demasiado alto',
    )
    .required('Consumo es requerido'),

  optionalConsumption: yup
    .number()
    .min(0, 'El consumo no puede ser negativo')
    .max(
      VALIDATION_RULES.NUMERIC.MAX_CONSUMPTION,
      'El consumo parece demasiado alto',
    )
    .optional()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value,
    ),

  cost: yup
    .number()
    .positive('El costo debe ser un número positivo')
    .optional()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value,
    ),

  currency: yup
    .string()
    .oneOf([...SUPPORTED_CURRENCY_CODES], 'Moneda inválida')
    .required('Moneda es requerida'),

  // Date fields with range validation (1940-2200)
  date: yup
    .string()
    .required('Fecha es requerida')
    .test(
      'date-range',
      `La fecha debe estar entre ${VALIDATION_RULES.DATE.MIN_DATE} y ${VALIDATION_RULES.DATE.MAX_DATE}`,
      (value) => {
        if (!value) return true;
        const date = new Date(value);
        const minDate = new Date(VALIDATION_RULES.DATE.MIN_DATE);
        const maxDate = new Date(VALIDATION_RULES.DATE.MAX_DATE);
        return date >= minDate && date <= maxDate;
      },
    ),

  optionalDate: yup
    .string()
    .optional()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value,
    )
    .test(
      'date-range',
      `La fecha debe estar entre ${VALIDATION_RULES.DATE.MIN_DATE} y ${VALIDATION_RULES.DATE.MAX_DATE}`,
      (value) => {
        if (!value) return true;
        const date = new Date(value);
        const minDate = new Date(VALIDATION_RULES.DATE.MIN_DATE);
        const maxDate = new Date(VALIDATION_RULES.DATE.MAX_DATE);
        return date >= minDate && date <= maxDate;
      },
    ),

  dateRange: {
    startDate: yup
      .string()
      .required('Fecha de inicio es requerida')
      .test(
        'date-range',
        `La fecha debe estar entre ${VALIDATION_RULES.DATE.MIN_DATE} y ${VALIDATION_RULES.DATE.MAX_DATE}`,
        (value) => {
          if (!value) return true;
          const date = new Date(value);
          const minDate = new Date(VALIDATION_RULES.DATE.MIN_DATE);
          const maxDate = new Date(VALIDATION_RULES.DATE.MAX_DATE);
          return date >= minDate && date <= maxDate;
        },
      ),
    endDate: yup
      .string()
      .required('Fecha de fin es requerida')
      .test(
        'date-range',
        `La fecha debe estar entre ${VALIDATION_RULES.DATE.MIN_DATE} y ${VALIDATION_RULES.DATE.MAX_DATE}`,
        (value) => {
          if (!value) return true;
          const date = new Date(value);
          const minDate = new Date(VALIDATION_RULES.DATE.MIN_DATE);
          const maxDate = new Date(VALIDATION_RULES.DATE.MAX_DATE);
          return date >= minDate && date <= maxDate;
        },
      )
      .test(
        'is-after-start',
        'La fecha de fin debe ser posterior a la fecha de inicio',
        function (value) {
          const { startDate } = this.parent;
          if (!startDate || !value) return true;
          return new Date(value) >= new Date(startDate);
        },
      ),
  },

  // Service type
  serviceType: yup
    .string()
    .oneOf(
      [
        'electricidad',
        'agua',
        'gas',
        'residuos-reciclables',
        'residuos-organicos',
        'residuos-generales',
      ],
      'Tipo de servicio inválido',
    )
    .required('Tipo de servicio es requerido'),

  // Period type
  periodType: yup
    .string()
    .oneOf(['diario', 'semanal', 'mensual'], 'Tipo de período inválido')
    .required('Tipo de período es requerido'),

  // Notes and descriptions
  notes: yup
    .string()
    .max(500, 'Las notas no pueden tener más de 500 caracteres')
    .trim(),

  description: yup
    .string()
    .max(1000, 'La descripción no puede tener más de 1000 caracteres')
    .trim(),

  // Invoice fields
  invoiceNumber: yup
    .string()
    .max(50, 'El número de factura no puede tener más de 50 caracteres')
    .trim(),

  vendorName: yup
    .string()
    .max(100, 'El nombre del proveedor no puede tener más de 100 caracteres')
    .trim(),

  supplyId: yup
    .string()
    .max(50, 'El ID de suministro no puede tener más de 50 caracteres')
    .trim(),
};

// ============================================================================
// Form Validation Schemas
// ============================================================================

// Authentication schemas
export const authSchemas = {
  login: yup.object({
    email: commonSchemas.email,
    password: commonSchemas.password,
  }),

  register: yup.object({
    name: commonSchemas.name,
    email: commonSchemas.email,
    password: commonSchemas.strongPassword,
    confirmPassword: commonSchemas.confirmPassword(),
    acceptTerms: yup
      .boolean()
      .oneOf([true], 'Debes aceptar los términos y condiciones'),
  }),

  forgotPassword: yup.object({
    email: commonSchemas.email,
  }),

  resetPassword: yup.object({
    password: commonSchemas.strongPassword,
    confirmPassword: commonSchemas.confirmPassword(),
    token: yup.string().required('Token es requerido'),
  }),

  devLogin: yup.object({
    username: yup.string().required('Usuario es requerido'),
    password: yup.string().required('Contraseña es requerida'),
  }),
};

// Profile schemas
export const profileSchemas = {
  restaurantProfile: yup.object({
    restaurant_name: commonSchemas.restaurantName,
    country: commonSchemas.country,
    city: commonSchemas.city,
    cuisine_type: commonSchemas.cuisineType,
    daily_covers: commonSchemas.dailyCovers,
    currency: yup
      .string()
      .length(3, 'Código de moneda debe tener 3 caracteres')
      .required('Moneda es requerida'),
    address: commonSchemas.address
      .optional()
      .transform((value, original) => (original === '' ? undefined : value))
      .default(undefined),
    description: commonSchemas.description
      .optional()
      .transform((value, original) => (original === '' ? undefined : value))
      .default(undefined),
    website: yup
      .string()
      .trim()
      .optional()
      .transform((value, original) =>
        original === '' || value === '' ? undefined : value,
      )
      .default(undefined)
      .test(
        'valid-website-format',
        'URL inválida',
        (value) =>
          !value || /^(https?:\/\/)?[\w.-]+(\.[\w.-]+)+.*$/.test(value),
      ),
    phone: commonSchemas.phone
      .optional()
      .transform((value, original) => (original === '' ? undefined : value))
      .default(undefined),
  }),

  userProfile: yup.object({
    name: commonSchemas.name.optional(),
    email: commonSchemas.email,
    phone: commonSchemas.phone.optional(),
    language: yup.string().oneOf(['es', 'en'], 'Idioma inválido').optional(),
    timezone: yup.string().optional(),
    notifications_enabled: yup.boolean().optional(),
  }),
};

// Manual entry schemas
export const entrySchemas = {
  manualEntry: yup
    .object({
      start_date: commonSchemas.dateRange.startDate,
      end_date: commonSchemas.dateRange.endDate,
      kwh_electricity: commonSchemas.optionalConsumption
        .optional()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === undefined
            ? undefined
            : value,
        )
        .default(undefined),
      m3_water: commonSchemas.optionalConsumption
        .optional()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === undefined
            ? undefined
            : value,
        )
        .default(undefined),
      m3_gas: commonSchemas.optionalConsumption
        .optional()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === undefined
            ? undefined
            : value,
        )
        .default(undefined),
      notes: commonSchemas.notes
        .optional()
        .transform((value, originalValue) =>
          originalValue === '' ? undefined : value,
        )
        .default(undefined),
    })
    .test(
      'at-least-one-consumption',
      'Debe ingresar al menos un tipo de consumo',
      (value) => {
        const { kwh_electricity, m3_water, m3_gas } = value;
        return !!(kwh_electricity || m3_water || m3_gas);
      },
    ) as yup.ObjectSchema<ManualEntryForm>,

  singleConsumptionEntry: yup.object({
    service_type: commonSchemas.serviceType,
    consumption: commonSchemas.consumption,
    unit: yup.string().required('Unidad es requerida'),
    start_date: commonSchemas.dateRange.startDate,
    end_date: commonSchemas.dateRange.endDate,
    cost: commonSchemas.cost,
    currency: commonSchemas.currency,
    notes: commonSchemas.notes,
  }),

  clientesEntry: yup.object({
    fecha: commonSchemas.date,
    periodo: commonSchemas.periodType,
    total_clientes: yup
      .number()
      .positive('El número de clientes debe ser positivo')
      .integer('El número de clientes debe ser un entero')
      .min(
        VALIDATION_RULES.NUMERIC.MIN_POSITIVE_INTEGER,
        'El número de clientes debe ser mayor a 0',
      )
      .required('Total de clientes es requerido'),
    total_cubiertos: yup
      .number()
      .positive('El número de cubiertos debe ser positivo')
      .integer('El número de cubiertos debe ser un entero')
      .min(
        VALIDATION_RULES.NUMERIC.MIN_POSITIVE_INTEGER,
        'El número de cubiertos debe ser mayor a 0',
      )
      .required('Total de cubiertos es requerido'),
    notas: commonSchemas.notes,
  }),
};

// Invoice schemas
export const invoiceSchemas = {
  invoiceEdit: yup.object({
    service_type: commonSchemas.serviceType,
    consumption: commonSchemas.consumption,
    consumption_unit: yup
      .string<ConsumptionUnit>()
      .oneOf(['kWh', 'm³', 'kg', 'bolsas'], 'Unidad de consumo no válida')
      .required('Unidad de consumo es requerida'),
    bag_size: yup
      .string<BagSize>()
      .oneOf(['small', 'medium', 'big'], 'Selecciona un tamaño de bolsa válido')
      .when('consumption_unit', {
        is: (value: string) => value === 'bolsas',
        then: (schema) => schema.required('Selecciona el tamaño de bolsa'),
        otherwise: (schema) => schema.optional(),
      }),
    start_date: commonSchemas.dateRange.startDate,
    end_date: commonSchemas.dateRange.endDate,
    cost_total: commonSchemas.cost,
    currency: commonSchemas.currency,
    vendor_name: commonSchemas.vendorName,
    invoice_number: commonSchemas.invoiceNumber,
    issued_date: commonSchemas.optionalDate, // Fixed: made optional
    supply_id: commonSchemas.supplyId,
    notes: commonSchemas.notes
      .optional()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value,
      )
      .default(undefined),
  }),

  invoiceSearch: yup.object({
    service_type: yup
      .string()
      .oneOf([
        'electricidad',
        'agua',
        'gas',
        'residuos-reciclables',
        'residuos-organicos',
        'residuos-generales',
      ]),
    date_from: yup.string(),
    date_to: yup.string(),
    vendor_name: yup.string(),
    min_consumption: yup
      .number()
      .positive()
      .integer()
      .min(VALIDATION_RULES.NUMERIC.MIN_POSITIVE_INTEGER),
    max_consumption: yup
      .number()
      .positive()
      .integer()
      .min(VALIDATION_RULES.NUMERIC.MIN_POSITIVE_INTEGER),
    min_cost: yup.number().positive(),
    max_cost: yup.number().positive(),
  }),
};

// Supplier schemas
export const supplierSchemas = {
  supplierCreate: yup.object({
    name: yup
      .string()
      .min(2, 'El nombre debe tener al menos 2 caracteres')
      .max(255, 'El nombre no puede exceder 255 caracteres')
      .required('Nombre es requerido'),
    description: commonSchemas.description
      .optional()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value,
      )
      .default(undefined),
    has_certification: yup.boolean().required('Certificación es requerida'),
    status: yup
      .string()
      .oneOf(['active', 'inactive'], 'Estado inválido')
      .required('Estado es requerido'),
  }),

  supplierUpdate: yup.object({
    name: yup
      .string()
      .min(2, 'El nombre debe tener al menos 2 caracteres')
      .max(255, 'El nombre no puede exceder 255 caracteres')
      .optional(),
    description: commonSchemas.description
      .optional()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value,
      )
      .default(undefined),
    has_certification: yup.boolean().optional(),
    status: yup
      .string()
      .oneOf(['active', 'inactive'], 'Estado inválido')
      .optional(),
  }),
};

// Sales document schemas
export const salesDocumentSchemas = {
  salesConfirmation: yup.object({
    period_start: yup
      .string()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Formato de fecha inválido. Use YYYY-MM-DD',
      )
      .required('Fecha de inicio es requerida'),
    period_end: yup
      .string()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Formato de fecha inválido. Use YYYY-MM-DD',
      )
      .required('Fecha de fin es requerida')
      .test(
        'end-after-start',
        'La fecha de fin debe ser posterior a la fecha de inicio',
        function (value) {
          const { period_start } = this.parent;
          if (!value || !period_start) return true;
          return new Date(value) >= new Date(period_start);
        },
      ),
    covers_count: yup
      .number()
      .positive('El número de cubiertos debe ser positivo')
      .integer('El número de cubiertos debe ser un entero')
      .min(1, 'El número de cubiertos debe ser mayor a 0')
      .max(10000, 'El número de cubiertos parece demasiado alto')
      .required('El número de cubiertos es requerido'),
    source: yup
      .string()
      .oneOf(['ocr', 'manual'], 'Fuente inválida')
      .default('ocr'),
    category: yup
      .string()
      .max(100, 'La categoría no puede superar los 100 caracteres')
      .optional()
      .nullable(),
    shift: yup
      .string()
      .max(100, 'El turno no puede superar los 100 caracteres')
      .optional()
      .nullable(),
    corrected_fields: yup.object().optional().nullable(),
    filename: yup
      .string()
      .max(255, 'El nombre del archivo es demasiado largo')
      .optional()
      .nullable(),
  }),

  salesDocumentEdit: yup.object({
    period_start: yup
      .string()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Formato de fecha inválido. Use YYYY-MM-DD',
      )
      .required('Fecha de inicio es requerida'),
    period_end: yup
      .string()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Formato de fecha inválido. Use YYYY-MM-DD',
      )
      .required('Fecha de fin es requerida')
      .test(
        'end-after-start',
        'La fecha de fin debe ser posterior a la fecha de inicio',
        function (value) {
          const { period_start } = this.parent;
          if (!value || !period_start) return true;
          return new Date(value) >= new Date(period_start);
        },
      ),
    covers_count: yup
      .number()
      .min(0, 'El número de cubiertos debe ser mayor o igual a 0')
      .max(100000, 'El número de cubiertos parece demasiado alto')
      .required('El número de cubiertos es requerido'),
    category: yup
      .string()
      .max(100, 'La categoría no puede superar los 100 caracteres')
      .nullable()
      .notRequired(),
    shift: yup
      .string()
      .max(100, 'El turno no puede superar los 100 caracteres')
      .nullable()
      .notRequired(),
  }),
};

// Settings schemas
export const settingsSchemas = {
  notificationSettings: yup.object({
    email_notifications: yup.boolean().required(),
    sms_notifications: yup.boolean(),
    weekly_summary: yup.boolean().required(),
    monthly_report: yup.boolean().required(),
    threshold_alerts: yup.boolean().required(),
    consumption_thresholds: yup.object({
      electricity_kwh: yup
        .number()
        .positive()
        .integer()
        .min(VALIDATION_RULES.NUMERIC.MIN_POSITIVE_INTEGER),
      water_m3: yup
        .number()
        .positive()
        .integer()
        .min(VALIDATION_RULES.NUMERIC.MIN_POSITIVE_INTEGER),
      gas_m3: yup
        .number()
        .positive()
        .integer()
        .min(VALIDATION_RULES.NUMERIC.MIN_POSITIVE_INTEGER),
    }),
  }),

  preferences: yup.object({
    language: yup.string().oneOf(['es', 'en']).required(),
    timezone: yup.string().required(),
    currency: yup.string().oneOf(['EUR', 'USD', 'MXN']).required(),
    date_format: yup
      .string()
      .oneOf(['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'])
      .required(),
    number_format: yup.string().oneOf(['european', 'american']).required(),
    dashboard_layout: yup.string().oneOf(['compact', 'detailed']),
  }),
};

// ============================================================================
// Validation Utilities
// ============================================================================

/**
 * Validates a form field value against a schema
 */
export const validateField = async (
  schema: yup.AnySchema,
  value: unknown,
): Promise<string | null> => {
  try {
    await schema.validate(value);
    return null;
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return error.message;
    }
    return 'Valor inválido';
  }
};

/**
 * Validates an entire form object against a schema
 */
export const validateForm = async <T extends Record<string, unknown>>(
  schema: yup.ObjectSchema<T>,
  values: T,
): Promise<{ isValid: boolean; errors: Partial<Record<keyof T, string>> }> => {
  try {
    await schema.validate(values, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: Partial<Record<keyof T, string>> = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path as keyof T] = err.message;
        }
      });
      return { isValid: false, errors };
    }
    return { isValid: false, errors: {} };
  }
};

/**
 * Creates a validation resolver for react-hook-form
 */
export const createYupResolver = <T extends Record<string, unknown>>(
  schema: yup.ObjectSchema<T>,
) => {
  return async (values: T) => {
    try {
      const validatedValues = await schema.validate(values, {
        abortEarly: false,
      });
      return { values: validatedValues, errors: {} };
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const errors: Record<string, { type: string; message: string }> = {};
        error.inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = {
              type: 'validation',
              message: err.message,
            };
          }
        });
        return { values: {}, errors };
      }
      return { values: {}, errors: {} };
    }
  };
};

/**
 * Conditional validation schema creator
 */
export const createConditionalSchema = <T extends Record<string, unknown>>(
  baseSchema: yup.ObjectSchema<T>,
  conditions: Array<{
    when: (values: T) => boolean;
    then: yup.ObjectSchema<Partial<T>>;
  }>,
) => {
  return baseSchema.test(
    'conditional',
    'Validation failed',
    function (value: unknown) {
      if (!value) return true;

      // Type assertion to handle yup's internal type transformations
      const typedValue = value as T;

      for (const condition of conditions) {
        if (condition.when(typedValue)) {
          try {
            condition.then.validateSync(typedValue, { abortEarly: false });
          } catch (error) {
            if (error instanceof yup.ValidationError) {
              return this.createError({
                path: error.path || '',
                message: error.message,
              });
            }
          }
        }
      }

      return true;
    },
  );
};

/**
 * Schema composition utility
 */
export const composeSchemas = <T extends Record<string, unknown>>(
  ...schemas: yup.ObjectSchema<Partial<T>>[]
): yup.ObjectSchema<Partial<T>> => {
  if (schemas.length === 0) {
    return yup.object() as yup.ObjectSchema<Partial<T>>;
  }

  if (schemas.length === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return schemas[0]!;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  let result = schemas[0]!;
  for (let i = 1; i < schemas.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    result = result.concat(schemas[i]!) as yup.ObjectSchema<Partial<T>>;
  }

  return result;
};

// ============================================================================
// Export all schemas as a convenience object
// ============================================================================

export const validationSchemas = {
  ...authSchemas,
  ...profileSchemas,
  ...entrySchemas,
  ...invoiceSchemas,
  ...salesDocumentSchemas,
  ...supplierSchemas,
  ...settingsSchemas,
} as const;

export default validationSchemas;
